import { useEffect, useState } from "react";
import api from "../services/api";
import { CalendarClock, FileText, Download, Loader2, Plus, Trash2, Play, Clock, BarChart3, Package, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const REPORT_TYPES = [
  { value: "revenue", label: "Revenue Report", icon: BarChart3, color: "text-emerald-600 bg-emerald-50" },
  { value: "orders", label: "Orders Report", icon: Package, color: "text-[#FF6B00] bg-orange-50" },
  { value: "agents", label: "Agents Report", icon: Users, color: "text-blue-600 bg-blue-50" },
];

const FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

export default function AdminReports() {
  const [schedules, setSchedules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newType, setNewType] = useState("revenue");
  const [newFreq, setNewFreq] = useState("weekly");
  const [expandedReport, setExpandedReport] = useState(null);

  const fetchData = async () => {
    try {
      const [sRes, hRes] = await Promise.all([
        api.get("/admin/reports/schedules"),
        api.get("/admin/reports/history"),
      ]);
      setSchedules(sRes.data);
      setHistory(hRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const addSchedule = async () => {
    try {
      await api.post("/admin/reports/schedules", { report_type: newType, frequency: newFreq, enabled: true });
      toast.success("Schedule created");
      setShowAdd(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create schedule");
    }
  };

  const deleteSchedule = async (type, freq) => {
    try {
      await api.delete(`/admin/reports/schedules/${type}/${freq}`);
      toast.success("Schedule deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const generateNow = async (type, freq) => {
    setGenerating(`${type}-${freq}`);
    try {
      await api.post("/admin/reports/generate", { report_type: type, frequency: freq });
      toast.success("Report generated!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate");
    }
    setGenerating(null);
  };

  const getTypeConfig = (type) => REPORT_TYPES.find((r) => r.value === type) || REPORT_TYPES[0];

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Scheduled Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Automate weekly & monthly reports</p>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl gap-2" data-testid="add-schedule-btn">
          <Plus className="h-4 w-4" /> Add Schedule
        </Button>
      </div>

      {/* Add Schedule Form */}
      {showAdd && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 animate-fadeInUp" data-testid="add-schedule-form">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Report Type</label>
              <select value={newType} onChange={(e) => setNewType(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="schedule-type-select">
                {REPORT_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Frequency</label>
              <select value={newFreq} onChange={(e) => setNewFreq(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="schedule-freq-select">
                {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2 mt-auto">
              <Button onClick={addSchedule} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl" data-testid="save-schedule-btn">Save</Button>
              <Button variant="outline" onClick={() => setShowAdd(false)} className="rounded-xl">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Active Schedules */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100" data-testid="schedules-table">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900 flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#FF6B00]" /> Active Schedules</h2>
        </div>
        {schedules.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarClock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">No schedules yet</p>
            <p className="text-sm text-slate-400 mt-1">Create your first automated report</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100 bg-slate-50/60">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-5">Report</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Frequency</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Last Generated</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Next Due</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map((s) => {
                  const cfg = getTypeConfig(s.report_type);
                  const Icon = cfg.icon;
                  return (
                    <TableRow key={`${s.report_type}-${s.frequency}`} className="border-slate-50">
                      <TableCell className="pl-5">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.color}`}><Icon className="h-4 w-4" /></div>
                          <span className="text-sm font-semibold text-slate-900">{cfg.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 text-xs font-medium text-slate-700">
                          <Clock className="h-3 w-3" /> {s.frequency}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{s.last_generated ? new Date(s.last_generated).toLocaleString() : "Never"}</TableCell>
                      <TableCell className="text-xs text-slate-500">{s.next_due ? new Date(s.next_due).toLocaleDateString() : "—"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => generateNow(s.report_type, s.frequency)}
                            disabled={generating === `${s.report_type}-${s.frequency}`}
                            className="rounded-lg gap-1 text-xs h-8"
                            data-testid={`generate-${s.report_type}-${s.frequency}`}
                          >
                            {generating === `${s.report_type}-${s.frequency}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                            Generate
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteSchedule(s.report_type, s.frequency)} className="rounded-lg h-8 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-${s.report_type}-${s.frequency}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Report History */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100" data-testid="reports-history">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900 flex items-center gap-2"><FileText className="h-5 w-5 text-[#FF6B00]" /> Generated Reports</h2>
        </div>
        {history.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">No reports generated yet</p>
            <p className="text-sm text-slate-400 mt-1">Create a schedule and generate your first report</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {history.map((r, i) => {
              const cfg = getTypeConfig(r.report_type);
              const Icon = cfg.icon;
              const isExpanded = expandedReport === i;
              const reportKey = r.id || r._id || `${r.report_type}-${r.generated_at}`;
              return (
                <div key={reportKey} className="p-4 hover:bg-slate-50/50 cursor-pointer transition-colors" onClick={() => setExpandedReport(isExpanded ? null : i)} data-testid={`report-item-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cfg.color}`}><Icon className="h-4 w-4" /></div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{cfg.label} ({r.frequency})</p>
                        <p className="text-xs text-slate-400">{r.period}</p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">{new Date(r.generated_at).toLocaleString()}</span>
                  </div>
                  {isExpanded && r.data && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-xl text-xs space-y-2 animate-fadeInUp" data-testid={`report-data-${i}`}>
                      {Object.entries(r.data).map(([key, val]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-slate-500 capitalize">{key.replace(/_/g, " ")}</span>
                          <span className="font-medium text-slate-800">
                            {typeof val === "number" ? val.toLocaleString() : Array.isArray(val) ? `${val.length} items` : String(val)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
